import React, { useState } from 'react';
import { Phone, Mail, MapPin, Shield, Zap, ChevronRight, ChevronLeft, Sun, Moon } from 'lucide-react';
import './App.css';

// --- DATA CONFIGURATION ---
const exteriorImages = [
  "/Front.jpg", 
  "/Angle.jpg", 
  "/Side.jpg", 
  "/Rear.jpg" 
];

const interiorImages = [
  "/Interior1.jpg", 
  "/Interior2.jpg", 
  "/Interior4.jpg",
  "/Interior5.webp"
];

function App() {
  const [currentAngleIndex, setCurrentAngleIndex] = useState(0);

  const handleWhatsApp = (plan) => {
    let message = "Hello,%20I%20am%20interested%20in%20booking%20the%20BYD%20VIP%20service.";
    if (plan) {
      message = `Hello,%20I%20would%20like%20to%20book%20the%20${plan}%20VIP%20service.`;
    }
    window.open(`https://wa.me/251943874973?text=${message}`, '_blank');
  };

  const rotateCar = (direction) => {
    if (direction === 'next') {
      setCurrentAngleIndex((prev) => (prev + 1) % exteriorImages.length);
    } else {
      setCurrentAngleIndex((prev) => (prev - 1 + exteriorImages.length) % exteriorImages.length);
    }
  };

  const handleSliderChange = (e) => {
    const value = e.target.value;
    const index = Math.floor((value / 100) * (exteriorImages.length - 1));
    setCurrentAngleIndex(index);
  };

  return (
    <div className="app-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="logo">ROYAL RIDES ET</div>
        <div className="nav-links">
          <a href="#home">Home</a>
          <a href="#fleet">The Car</a>
          <a href="#interior">Interiors</a>
          <a href="#rates">Rates</a>
          <a href="#contact">Contact</a>
          <button className="book-btn-nav" onClick={() => handleWhatsApp()}>Book Now</button>
        </div>
      </nav>

      {/* Hero Section - SEO: Strong H1 with location keyword */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1>VIP Transportation in Addis Ababa</h1>
          <p>
            Experience Ethiopia in ultimate comfort. We provide professional 
            <strong> private chauffeur services</strong> for business, diplomats, and airport transfers 
            featuring the all-new electric <strong>BYD YUAN UP</strong>.
          </p>
          <div className="hero-buttons">
            <button className="primary-btn" onClick={() => handleWhatsApp()}>Book via WhatsApp</button>
            <a href="#contact" className="secondary-btn">Contact Us</a>
          </div>
        </div>
      </section>

      {/* 360 Car Viewer Section */}
      <section id="fleet" className="fleet-section">
        <div className="section-header">
          <h2>Our Fleet: Electric Car</h2>
          <div className="divider"></div>
          <p>Modern elegance meets sustainable luxury travel.</p>
        </div>

        <div className="car-showcase-360">
          <div className="viewer-container">
            {/* SEO: Descriptive Alt Tag */}
            <img 
              src={process.env.PUBLIC_URL + exteriorImages[currentAngleIndex]} 
              alt="BYD Yuan Up Luxury Car Exterior Addis Ababa" 
              className="car-360-img" 
            />
            
            <button className="rotate-btn left" onClick={() => rotateCar('prev')} aria-label="Rotate Left"><ChevronLeft size={24} /></button>
            <button className="rotate-btn right" onClick={() => rotateCar('next')} aria-label="Rotate Right"><ChevronRight size={24} /></button>
            
            <div className="slider-container-overlay">
              <input 
                type="range" 
                min="0" 
                max="100" 
                defaultValue="0" 
                className="rotation-slider"
                onChange={handleSliderChange}
                aria-label="Rotate Car View"
              />
            </div>
          </div>

          <div className="car-details-grid">
            <div className="feature-card">
              <Zap className="icon" size={20} />
              <h3>100% Electric & Silent</h3>
              <p>Eco-friendly travel with zero noise pollution.</p>
            </div>
            <div className="feature-card">
              <Shield className="icon" size={20} />
              <h3>VIP Safety Standards</h3>
              <p>Top-tier safety features for secure transport.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interior Gallery Section */}
      <section id="interior" className="interior-section">
        <div className="section-header">
          <h2>Premium Interior Comfort</h2>
          <div className="divider"></div>
          <p>Spacious seating, climate control, and modern amenities.</p>
        </div>

        <div className="interior-gallery">
          {interiorImages.map((img, index) => (
            <div key={index} className="gallery-item">
              {/* SEO: Descriptive Alt Tag */}
              <img src={process.env.PUBLIC_URL + img} alt={`Luxury Car Interior Leather Seats - View ${index + 1}`} />
              <div className="overlay">
                <span>VIP Comfort</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rates Section - SEO: Mentioning specific services */}
      <section id="rates" className="rates-section">
        <div className="section-header">
          <h2>Service Rates & Packages</h2>
          <div className="divider"></div>
          <p>Transparent pricing for Executive and Tourist Transport.</p>
        </div>

        <div className="rates-container">
          {/* Day Rate Card */}
          <div className="rate-card day">
            <div className="rate-icon-box"><Sun size={32} /></div>
            <h3>Full Day Chauffeur</h3>
            <p className="time-frame">Morning - Evening</p>
            <div className="price-tag">$35</div>
            <p className="rate-desc">
              The best choice for <strong>tourist sightseeing</strong>, visiting museums, Unity Park, Entoto Park, and <strong>city tours</strong>.
            </p>
            <button className="rate-btn" onClick={() => handleWhatsApp('Day ($35)')}>Book Day Service</button>
          </div>

          {/* Night Rate Card */}
          <div className="rate-card night">
            <div className="rate-icon-box"><Moon size={32} /></div>
            <h3>Night & Airport Transfer</h3>
            <p className="time-frame">Evening - Morning</p>
            <div className="price-tag">$50</div>
            <p className="rate-desc">
              Secure transportation for VIP dinners, events, and <strong>Bole International Airport</strong> pickups/drop-offs.
            </p>
            <button className="rate-btn" onClick={() => handleWhatsApp('Night ($50)')}>Book Night Service</button>
          </div>
        </div>
      </section>

      {/* Location & Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          
          <div className="contact-info">
            <h2>Contact Royal Rides</h2>
            <p>Book your private driver in Ethiopia today.</p>
            
            <div className="info-item">
              <Phone className="contact-icon" />
              <div>
                <h3>Phone / WhatsApp</h3>
                <p><a href="tel:+251943874973">+251 943 874 973</a></p>
                <p><a href="tel:+251930000293">+251 930 000 293</a></p>
              </div>
            </div>

            <div className="info-item">
              <Mail className="contact-icon" />
              <div>
                <h3>Email Booking</h3>
                <p><a href="mailto:yabsiratamiru5@gmail.com">yabsiratamiru5@gmail.com</a></p>
              </div>
            </div>

            <div className="info-item">
              <MapPin className="contact-icon" />
              <div>
                <h3>Service Area</h3>
                <p>Bole, Addis Ababa, Ethiopia</p>
              </div>
            </div>
          </div>

          <div className="map-container">
  <iframe 
    src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d246.29520318591128!2d38.816061183981326!3d8.997593186047181!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2set!4v1764178893558!5m2!1sen!2set" 
    width="100%" 
    height="400" 
    // I removed the 'filter' part here:
    style={{ border: 0, borderRadius: '15px' }} 
    allowFullScreen="" 
    loading="lazy" 
    referrerPolicy="no-referrer-when-downgrade"
    title="Royal Rides Location Addis Ababa"
  ></iframe>
</div>
        </div>
      </section>

      <footer>
        <p>© {new Date().getFullYear()} Royal Rides ET. Premium VIP Transportation in Ethiopia.</p>
      </footer>
    </div>
  );
}

export default App;